import { useEffect, useState, useCallback } from "react";
import { X, ChevronUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";

const CATS = ["all", "level", "boss", "feature"];
const CAT_COLOR = { level: "#4CAF50", boss: "#E91E63", feature: "#0067C6" };

export default function SuggestionsModal({ onClose, onAuth }) {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cat, setCat] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "feature", title: "", text: "" });
  const [voted, setVoted] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.listSuggestions(cat).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [cat]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.title.trim().length < 2) return;
    try {
      await api.createSuggestion({ ...form, author: user?.username || "Anónimo" });
      toast.success(t("ideaThanks"));
      setForm({ category: "feature", title: "", text: "" });
      setShowForm(false);
      setCat("all"); load();
    } catch (e) { toast.error("Error"); }
  };

  const vote = async (id) => {
    if (voted[id]) return;
    setVoted((v) => ({ ...v, [id]: true }));
    setItems((arr) => arr.map((s) => (s.id === id ? { ...s, votes: s.votes + 1 } : s)).sort((a, b) => b.votes - a.votes));
    try { await api.voteSuggestion(id); } catch (e) {}
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDeleteIdea"))) return;
    setItems((arr) => arr.filter((s) => s.id !== id));
    try { await api.deleteSuggestion(id); toast.success(t("ideaDeleted")); }
    catch (e) { toast.error("Error"); load(); }
  };

  const delAll = async () => {
    if (!window.confirm(t("confirmDeleteAllIdeas"))) return;
    try { const r = await api.deleteAllSuggestions(); toast.success(t("ideaDeleted")); setItems([]); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <div className="absolute inset-0 z-[88] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/85 p-4 scanlines" data-testid="suggestions-modal">
      <div className="pixel-card bg-[#141414] p-5 md:p-7 w-full max-w-lg max-h-full flex flex-col pop-in my-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-pixel text-base md:text-lg text-[#FFEB3B] pixel-stroke">💡 {t("ideasTitle")}</h2>
          <div className="flex items-center gap-2">
            {isAdmin && items.length > 0 && (
              <button data-testid="sug-clear-all-btn" onClick={delAll} className="pixel-btn bg-[#b71c1c] text-white p-2" title={t("clearAllIdeas")}>
                <Trash2 size={15} strokeWidth={2.5} />
              </button>
            )}
            <button data-testid="suggestions-close-btn" onClick={onClose} className="pixel-btn bg-black/55 text-white p-2"><X size={16} strokeWidth={2.5} /></button>
          </div>
        </div>

        <div className="flex gap-2 mb-3 flex-wrap">
          {CATS.map((c) => (
            <button key={c} data-testid={`sug-cat-${c}`} onClick={() => setCat(c)} className="pixel-btn px-3 py-1.5 text-[9px]"
              style={{ backgroundColor: cat === c ? "#FF5722" : "#1b1b1b", color: "#fff", opacity: cat === c ? 1 : 0.7 }}>
              {t(`cat_${c}`)}
            </button>
          ))}
        </div>

        {!user ? (
          <button data-testid="sug-signin-btn" onClick={onAuth} className="pixel-btn bg-[#FFB300] text-black px-4 py-2.5 text-xs mb-3 flex items-center justify-center gap-2">
            <Plus size={16} strokeWidth={3} /> {t("suggestSignIn")}
          </button>
        ) : showForm ? (
          <div className="flex flex-col gap-2 border-2 border-white/15 rounded-xl p-3 mb-3">
            <div className="flex gap-2">
              {["level", "boss", "feature"].map((c) => (
                <button key={c} data-testid={`sug-form-cat-${c}`} onClick={() => setForm((f) => ({ ...f, category: c }))} className="pixel-btn px-3 py-1.5 text-[9px] flex-1"
                  style={{ backgroundColor: form.category === c ? CAT_COLOR[c] : "#1b1b1b", color: "#fff" }}>{t(`cat_${c}`)}</button>
              ))}
            </div>
            <input data-testid="sug-title" value={form.title} maxLength={80} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t("ideaTitle")} className="font-vt text-lg bg-white text-black rounded-lg px-3 py-2 outline-none" />
            <textarea data-testid="sug-text" value={form.text} maxLength={600} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              placeholder={t("ideaDesc")} rows={3} className="font-vt text-lg bg-white text-black rounded-lg px-3 py-2 outline-none resize-none" />
            <div className="flex gap-2">
              <button data-testid="sug-submit-btn" onClick={submit} className="pixel-btn bg-[#4CAF50] text-white px-4 py-2 text-xs flex-1">{t("send")}</button>
              <button onClick={() => setShowForm(false)} className="pixel-btn bg-[#444] text-white px-4 py-2 text-xs">{t("cancel")}</button>
            </div>
          </div>
        ) : (
          <button data-testid="sug-new-btn" onClick={() => setShowForm(true)} className="pixel-btn bg-[#FFB300] text-black px-4 py-2.5 text-xs mb-3 flex items-center justify-center gap-2">
            <Plus size={16} strokeWidth={3} /> {t("addIdea")}
          </button>
        )}

        <div className="overflow-y-auto flex-1 space-y-2 pr-1" data-testid="suggestions-list">
          {loading ? <p className="font-vt text-xl text-white/70 text-center py-6">{t("loading")}</p>
            : items.length === 0 ? <p className="font-vt text-xl text-white/60 text-center py-6">{t("noIdeas")}</p>
            : items.map((s) => (
              <div key={s.id} className="flex items-center gap-3 bg-[#1c1c1c] rounded-xl p-3">
                <button data-testid={`sug-vote-${s.id}`} onClick={() => vote(s.id)} disabled={voted[s.id]}
                  className="flex flex-col items-center justify-center min-w-[44px] rounded-lg py-1.5 bg-black/40 hover:bg-black/60 disabled:opacity-60">
                  <ChevronUp size={18} strokeWidth={3} className="text-[#FFEB3B]" />
                  <span className="font-pixel text-[10px] text-white">{s.votes}</span>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-pixel text-[7px] px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: CAT_COLOR[s.category] || "#666" }}>{t(`cat_${s.category}`)}</span>
                    <span className="font-vt text-lg text-white truncate">{s.title}</span>
                  </div>
                  {s.text && <p className="font-vt text-base text-white/60 leading-tight mt-0.5 line-clamp-2">{s.text}</p>}
                  <p className="font-vt text-sm text-white/35">— {s.author}</p>
                </div>
                {isAdmin && (
                  <button data-testid={`sug-delete-${s.id}`} onClick={() => del(s.id)} title={t("deleteIdea")}
                    className="pixel-btn bg-[#b71c1c] text-white p-2 self-start">
                    <Trash2 size={14} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
